import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import HeaderHome from '../components/layout/HeaderHome'; // Import the Header component
import NavigationButton from '../components/layout/Navigation'; // Import the Navigation component
import 'swiper/swiper-bundle.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, Autoplay } from 'swiper/modules';
import ShareButton from '../components/campaigns/ShareButton';
import { getDigitalProducts, getPopularSellers } from '../services/digitalProductApi';
import { getMediaUrl } from '../utils/mediaUtils';
import { forumApi } from '../services/forumApi';
import { parseSafeDate, formatSafeDate, isDateExpired } from '../utils/dateUtils';
import BrandPageLoader from '../components/common/BrandPageLoader';


function getCsrfToken() {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') {
      return value;
    }
  }
  return null;
}

const formatIDR = (amount) => {
  return 'Rp. ' + new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatIDRTarget = (amount) => {
  if (amount <= 0) return '\u221E';
  return 'Rp. ' + new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatIDRCourse = (amount) => {
  if (amount <= 0) return 'GRATIS';
  return 'Rp. ' + new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
  }).format(amount);
};

const isCampaignExpired = (deadline) => {
  if (!deadline) return false;
  return isDateExpired(deadline);
};

const formatDeadline = (deadline) => {
  if (!deadline) return 'tidak ada';
  return formatSafeDate(deadline, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }, 'tidak ada');
};


const getEventStatus = (startStr, endStr) => {
  const now = new Date();
  const start = parseSafeDate(startStr);
  if (!start) return { isFinished: false };
  const end = endStr ? parseSafeDate(endStr) : new Date(start.getTime() + 4 * 60 * 60 * 1000);
  return { isFinished: end ? now > end : false };
};


const getButtonLabel = (title = '', isShort = false) => {
  const lowerTitle = (title || '').toLowerCase();
  if (lowerTitle.includes('infak')) return isShort ? 'Infak' : 'Infak Sekarang';
  if (lowerTitle.includes('sedekah')) return isShort ? 'Sedekah' : 'Sedekah Sekarang';
  if (lowerTitle.includes('zakat')) return isShort ? 'Zakat' : 'Zakat Sekarang';
  return isShort ? 'Donasi' : 'Donasi Sekarang';
};

const getProductStock = (product) => {
  if (!product) return 0;
  if (product.variations && product.variations.length > 0) {
    const varStock = product.variations.reduce((sum, v) => sum + (v.is_active ? Number(v.stock || 0) : 0), 0);
    if (varStock > 0 || product.stock === undefined || product.stock === null) {
      return varStock;
    }
  }
  if (product.total_stock !== undefined && product.total_stock !== null) {
    return Number(product.total_stock);
  }
  return Number(product.stock || 0);
};

const sortProductsByPopularityAndStock = (items) => {
  if (!Array.isArray(items)) return [];
  return [...items].sort((a, b) => {
    const stockA = getProductStock(a);
    const stockB = getProductStock(b);
    const inStockA = stockA > 0 ? 1 : 0;
    const inStockB = stockB > 0 ? 1 : 0;

    // 1. In-stock products always take precedence over out-of-stock products
    if (inStockA !== inStockB) {
      return inStockB - inStockA;
    }

    // 2. Sort by popularity (Views and Likes)
    const popScoreA = (Number(a.views_count || 0) * 1) + (Number(a.likes_count || 0) * 3);
    const popScoreB = (Number(b.views_count || 0) * 1) + (Number(b.likes_count || 0) * 3);
    if (popScoreB !== popScoreA) {
      return popScoreB - popScoreA;
    }

    const likesA = Number(a.likes_count || 0);
    const likesB = Number(b.likes_count || 0);
    if (likesB !== likesA) {
      return likesB - likesA;
    }

    const viewsA = Number(a.views_count || 0);
    const viewsB = Number(b.views_count || 0);
    if (viewsB !== viewsA) {
      return viewsB - viewsA;
    }

    // 3. Fallback: Newest
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const [campaigns, setCampaigns] = useState([]);
  const [featuredCampaigns, setFeaturedCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [featuredProducts, setfeaturedProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [digitalProducts, setDigitalProducts] = useState([]);
  const [featuredDigitalProducts, setFeaturedDigitalProducts] = useState([]);
  const [popularSellers, setPopularSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSlideCampaign, setActiveSlideCampaign] = useState(0);
  const [activeSlideProduct, setActiveSlideProduct] = useState(0);
  const [activeSlideCourse, setActiveSlideCourse] = useState(0);
  const [activeSlideDigital, setActiveSlideDigital] = useState(0);
  const sliderIntervalCampaign = useRef(null);
  const sliderIntervalProduct = useRef(null);
  const sliderIntervalCourse = useRef(null);
  const sliderIntervalDigital = useRef(null);
  const searchTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({ content: '', rating: 5 });
  const [myTestimonial, setMyTestimonial] = useState(null);
  const [aboutUs, setAboutUs] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [events, setEvents] = useState([]);
  const [heroBanners, setHeroBanners] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [threads, setThreads] = useState([]);
  const [articles, setArticles] = useState([]);


  const handleSlideChange = (swiper) => {
    // Stop all videos in the slider
    const allVideos = document.querySelectorAll('.hero-video');
    allVideos.forEach(v => {
      v.pause();
      v.currentTime = 0;
    });

    // Start video in current slide if exists
    const activeSlide = swiper.slides[swiper.activeIndex];
    const currentVideo = activeSlide?.querySelector('video');
    if (currentVideo) {
      currentVideo.currentTime = 0;
      currentVideo.play().catch(err => console.log("Video play interrupted", err));
      swiper.autoplay.stop();
    } else {
      swiper.autoplay.start();
    }
  };

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products`);
        const featured = response.data.filter(product => product.is_featured === true);
        const sortedFeatured = sortProductsByPopularityAndStock(featured);
        setfeaturedProducts(sortedFeatured.slice(0, 4));
      } catch (err) {
        console.error('Error fetching featured products:', err);
        setError('Failed to load featured products');
      }
    };
    fetchFeaturedProducts();

    const fetchFeaturedCampaigns = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/campaigns`);
        const featuredCampaigns = response.data.filter(campaign => campaign.is_featured === true);
        setFeaturedCampaigns(featuredCampaigns.slice(0, 4));
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      }
    };
    fetchFeaturedCampaigns();

    const fetchFeaturedCourses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/courses`);
        const featuredCourses = response.data.filter(course => course.is_featured === true);
        setFeaturedCourses(featuredCourses.slice(0, 4));
      } catch (err) {
        console.error('Error fetching featured courses:', err);
        setError('Failed to load featured courses');
      }
    };
    fetchFeaturedCourses();

    const fetchFeaturedDigitalProducts = async () => {
      try {
        const response = await getDigitalProducts();
        // Featured digital ones? Logic per requirement or just top 4
        setFeaturedDigitalProducts(response.data.slice(0, 4));
      } catch (err) {
        console.error('Error fetching digital products:', err);
      }
    };
    fetchFeaturedDigitalProducts();

    const fetchPopularSellers = async () => {
      try {
        const response = await getPopularSellers();
        setPopularSellers(response.data);
      } catch (err) {
        console.error('Error fetching popular sellers:', err);
      }
    };
    fetchPopularSellers();

    const fetchSiteContent = async () => {
      try {
        const [testimonialsRes, partnersRes, activitiesRes, aboutUsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/testimonials/`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/partners/`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/activities/`),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/about-us/`)
        ]);
        setTestimonials((testimonialsRes.data.results || testimonialsRes.data).filter(t => t.is_approved));
        setPartners(partnersRes.data.results || partnersRes.data);
        setActivities((activitiesRes.data.results || activitiesRes.data).slice(0, 3));
        const aboutData = aboutUsRes.data.results || aboutUsRes.data;
        if (Array.isArray(aboutData) && aboutData.length > 0) {
          setAboutUs(aboutData[0]);
        } else if (aboutData && !Array.isArray(aboutData)) {
          setAboutUs(aboutData);
        }

        const user = JSON.parse(localStorage.getItem('user'));
        if (user?.access) {
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/testimonials/my_testimonial/`, {
            headers: { Authorization: `Bearer ${user.access}` }
          }).then(res => {
            if (res.data.id) {
              setMyTestimonial(res.data);
              setTestimonialForm({ content: res.data.content, rating: res.data.rating });
            }
          }).catch(err => console.error("Error fetching my testimonial:", err));
        }
        
        // Fetch latest forum threads for mobile carousel
        forumApi.getThreads().then(res => {
          setThreads(res.data.slice(0, 6));
        }).catch(err => console.error("Error fetching threads:", err));

        // Fetch latest articles for mobile carousel
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/articles/`).then(res => {
          setArticles(res.data.slice(0, 6));
        }).catch(err => console.error("Error fetching articles:", err));

      } catch (err) {
        console.error("Error fetching site content:", err);
      }
    };
    fetchSiteContent();

    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/events/`);
        setEvents(response.data.results || response.data);
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };
    fetchEvents();

    const fetchHeroBanners = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/hero-banners/`);
        setHeroBanners(Array.isArray(response.data) ? response.data.filter(b => b.is_active) : []);
      } catch (err) {
        console.error('Error fetching hero banners:', err);
      } finally {
        setTimeout(() => setLoading(false), 500);
      }
    };
    fetchHeroBanners();
  }, []);

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    const user = JSON.parse(localStorage.getItem('user'));
    const token = user?.access;
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/site-content/testimonials/my_testimonial/`, testimonialForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyTestimonial(res.data);
      alert(myTestimonial ? "Testimoni berhasil diperbarui!" : "Terima kasih! Testimoni Anda sedang menunggu persetujuan admin.");
      setShowTestimonialModal(false);
    } catch (err) {
      alert(err.response?.data?.non_field_errors?.[0] || err.response?.data?.detail || "Gagal mengirim testimoni");
    }
  };

  // Fetch regular products (based on search query)
  const fetchProducts = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/products/`,
        { params: { search } }
      );
      setProducts(response.data); // Set regular products (search results)
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Fetch regular campaigns (based on search query)
  const fetchCampaigns = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/campaigns/`,
        { params: { search } }
      );
      const sortedData = response.data.sort((a, b) => {
        const isAExpired = isCampaignExpired(a.deadline);
        const isBExpired = isCampaignExpired(b.deadline);

        if (isAExpired && !isBExpired) return 1;
        if (!isAExpired && isBExpired) return -1;

        if (!a.deadline && b.deadline) return 1;
        if (a.deadline && !b.deadline) return -1;
        if (!a.deadline && !b.deadline) return 0;

        return new Date(a.deadline) - new Date(b.deadline);
      });
      setCampaigns(sortedData); // Set regular campaigns (search results)
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  // Fetch regular courses (based on search query)
  const fetchCourses = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/courses/`,
        { params: { search } }
      );
      setCourses(response.data); // Set regular courses (search results)
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchDigitalProducts = async (search = '') => {
    try {
      setLoading(true);
      const response = await getDigitalProducts();
      // Filter if needed
      setDigitalProducts(response.data);
    } catch (err) {
      console.error('Error fetching digital products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchCampaigns(query);
      fetchProducts(query);
      fetchCourses(query);
      fetchDigitalProducts(query);
    }, 500);
  };

  useEffect(() => {
    fetchCampaigns();
    fetchProducts();
    fetchCourses();
    fetchDigitalProducts();

    // Clean up function
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (sliderIntervalCampaign.current) clearInterval(sliderIntervalCampaign.current);
      if (sliderIntervalProduct.current) clearInterval(sliderIntervalProduct.current);
      if (sliderIntervalCourse.current) clearInterval(sliderIntervalCourse.current);
      if (sliderIntervalDigital.current) clearInterval(sliderIntervalDigital.current);
    };
  }, []);

  // Highlight campaigns: kombinasi featured, terpopuler, dan terbaru
  const highlightCampaigns = useMemo(() => {
    const list = campaigns && campaigns.length > 0 ? campaigns : featuredCampaigns;
    if (!list || list.length === 0) return [];

    const featured = list.filter(c => c.is_featured && !isCampaignExpired(c.deadline));
    const popular = [...list].sort((a, b) => {
      const popA = Number(a.current_amount || 0) + (Number(a.likes_count || 0) * 10000);
      const popB = Number(b.current_amount || 0) + (Number(b.likes_count || 0) * 10000);
      return popB - popA;
    }).slice(0, 3);
    const latest = [...list].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 3);

    const map = new Map();
    featured.forEach(c => map.set(c.id, c));
    popular.forEach(c => { if (!map.has(c.id)) map.set(c.id, c); });
    latest.forEach(c => { if (!map.has(c.id)) map.set(c.id, c); });

    const result = Array.from(map.values()).slice(0, 6);
    return result.length > 0 ? result : list.slice(0, 4);
  }, [campaigns, featuredCampaigns]);

  // Set up automatic sliders
  useEffect(() => {
    if (highlightCampaigns.length > 0) {
      sliderIntervalCampaign.current = setInterval(() => {
        setActiveSlideCampaign(prev => (prev + 1) % highlightCampaigns.length);
      }, 5000);
    }
    return () => { if (sliderIntervalCampaign.current) clearInterval(sliderIntervalCampaign.current); };
  }, [highlightCampaigns]);

  useEffect(() => {
    if (featuredProducts.length > 0) {
      sliderIntervalProduct.current = setInterval(() => {
        setActiveSlideProduct(prev => (prev + 1) % featuredProducts.length);
      }, 5000);
    }
    return () => { if (sliderIntervalProduct.current) clearInterval(sliderIntervalProduct.current); };
  }, [featuredProducts]);

  useEffect(() => {
    if (featuredCourses.length > 0) {
      sliderIntervalCourse.current = setInterval(() => {
        setActiveSlideCourse(prev => (prev + 1) % featuredCourses.length);
      }, 5000);
    }
    return () => { if (sliderIntervalCourse.current) clearInterval(sliderIntervalCourse.current); };
  }, [featuredCourses]);

  useEffect(() => {
    if (featuredDigitalProducts.length > 0) {
      sliderIntervalDigital.current = setInterval(() => {
        setActiveSlideDigital(prev => (prev + 1) % featuredDigitalProducts.length);
      }, 5000);
    }
    return () => { if (sliderIntervalDigital.current) clearInterval(sliderIntervalDigital.current); };
  }, [featuredDigitalProducts]);

  const goToSlideCampaign = (index) => {
    setActiveSlideCampaign(index);
    if (sliderIntervalCampaign.current) clearInterval(sliderIntervalCampaign.current);
    sliderIntervalCampaign.current = setInterval(() => {
      setActiveSlideCampaign(prev => (prev + 1) % (highlightCampaigns.length || 1));
    }, 5000);
  };

  const goToSlideProduct = (index) => {
    setActiveSlideProduct(index);
    if (sliderIntervalProduct.current) clearInterval(sliderIntervalProduct.current);
    sliderIntervalProduct.current = setInterval(() => {
      setActiveSlideProduct(prev => (prev + 1) % featuredProducts.length);
    }, 5000);
  };

  const goToSlideCourse = (index) => {
    setActiveSlideCourse(index);
    if (sliderIntervalCourse.current) clearInterval(sliderIntervalCourse.current);
    sliderIntervalCourse.current = setInterval(() => {
      setActiveSlideCourse(prev => (prev + 1) % featuredCourses.length);
    }, 5000);
  };

  const goToSlideDigital = (index) => {
    setActiveSlideDigital(index);
    if (sliderIntervalDigital.current) clearInterval(sliderIntervalDigital.current);
    sliderIntervalDigital.current = setInterval(() => {
      setActiveSlideDigital(prev => (prev + 1) % featuredDigitalProducts.length);
    }, 5000);
  };

  const addToCart = async (productId) => {
    const csrfToken = getCsrfToken();
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.access) {
        console.error('User not logged in');
        navigate('/login');
        return;
      }

      const product = featuredProducts.find(p => p.id === productId) || products.find(p => p.id === productId);
      if (product && product.variations && product.variations.length > 0) {
        navigate(`/produk/${product.slug || product.id}`);
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/carts/cart/`, {
        product_id: productId,
        quantity: 1
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          'X-CSRFToken': csrfToken,
        }
      });

      window.dispatchEvent(new Event('cartUpdated'));
      // Removed alert
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert('Gagal menambahkan ke Keranjang Belanja');
    }
  };

  const addToWishlist = async (productId) => {
    const csrfToken = getCsrfToken();
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user || !user.access) {
        console.error('User not logged in');
        navigate('/login');
        return;
      }

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/wishlists/wishlist/`, {
        product_id: productId
      }, {
        headers: {
          Authorization: `Bearer ${user.access}`,
          'X-CSRFToken': csrfToken,
        }
      });

      alert('Berhasil menambahkan ke Incaran!');
    } catch (error) {
      console.error('Error adding product to wishlist:', error);
      alert('Gagal menambahkan ke Incaran, ' + error['response']['data']['message']);
    }
  };

  const sortedCampaigns = campaigns;
  const sortedProducts = sortProductsByPopularityAndStock(products);
  const sortedCourses = [...courses].sort((a, b) => (b.price || 0) - (a.price || 0));
  const onlyPartners = partners.filter(p => !p.type || p.type === 'partner');
  const onlyMitra = partners.filter(p => p.type === 'mitra');

  // Hierarchy Tree Logic
  const buildTree = (records) => {
    if (!records) return [];
    const map = {};
    const roots = [];
    const sorted = [...records].sort((a, b) =>
      (a.hierarchy_code || "").localeCompare(b.hierarchy_code || "", undefined, { numeric: true, sensitivity: 'base' })
    );

    sorted.forEach(val => {
      map[val.hierarchy_code] = { ...val, children: [] };
      const parts = val.hierarchy_code.split('.');
      if (parts.length === 1) {
        roots.push(map[val.hierarchy_code]);
      } else {
        const parentKey = parts.slice(0, -1).join('.');
        if (map[parentKey]) {
          map[parentKey].children.push(map[val.hierarchy_code]);
        } else {
          roots.push(map[val.hierarchy_code]);
        }
      }
    });
    return roots;
  };

  const personnelTree = buildTree(aboutUs?.personnel);

  const SocialIcon = ({ type }) => {
    switch (type) {
      case 'instagram': return <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>;
      case 'facebook': return <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" /></svg>;
      case 'linkedin': return <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z" /></svg>;
      case 'twitter': return <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>;
      case 'whatsapp': return <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.888-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.347-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" /></svg>;
      default: return <span className="material-icons text-[20px]">language</span>;
    }
  };

  const PersonnelCard = ({ person }) => (
    <div className="flex flex-col items-center group w-full">
      <div className="bg-white p-4 md:p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 w-full min-w-[220px] max-w-[280px] text-center relative hover:shadow-2xl transition duration-500 transform hover:-translate-y-2 z-10">
        <div className="w-20 h-20 md:w-28 md:h-28 rounded-[2rem] aspect-square overflow-hidden mx-auto mb-4 shadow-xl border-4 border-white ring-4 ring-gray-50/50 group-hover:ring-green-50 transition-all">
          <img
            src={getMediaUrl(person.image)}
            alt={person.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(person.name) + '&background=0D8ABC&color=fff'; }}
          />
        </div>
        <h4 className="text-base md:text-lg font-black text-gray-900 mb-1 leading-tight">{person.name}</h4>
        <p className="text-[10px] font-black text-green-600 uppercase tracking-widest mb-4">{person.job_title}</p>

        {person.social_media?.length > 0 && (
          <div className="flex justify-center gap-4 mt-2 pt-4 border-t border-gray-50">
            {person.social_media.map((sm, i) => (
              <a key={i} href={sm.link} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-green-600 transition transform hover:scale-125 flex items-center justify-center">
                <SocialIcon type={sm.icon} />
              </a>
            ))}
          </div>
        )}
      </div>

      {person.children?.length > 0 && (
        <div className="relative pt-10 w-full flex flex-col items-center">
          <div className="absolute top-0 left-1/2 w-0.5 h-10 bg-green-200 -ml-[1px]"></div>

          <div className="flex justify-center flex-wrap sm:flex-nowrap relative w-full">
            {person.children.map((child, i) => (
              <div key={child.id} className="relative pt-8 px-4 sm:px-6 flex flex-col items-center flex-1 min-w-[240px] max-w-[320px]">
                {person.children.length > 1 && (
                  <>
                    {i === 0 && <div className="absolute top-0 left-1/2 right-0 h-0.5 bg-green-200"></div>}
                    {i === person.children.length - 1 && <div className="absolute top-0 left-0 right-1/2 h-0.5 bg-green-200"></div>}
                    {i > 0 && i < person.children.length - 1 && <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-200"></div>}
                  </>
                )}
                <div className="absolute top-0 left-1/2 w-0.5 h-8 bg-green-200 -ml-[1px]"></div>

                <PersonnelCard person={child} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="body relative">
      <BrandPageLoader isVisible={loading} />
      <Helmet>
        <title>Barakah Economy - Penguatan Sistem Ekonomi Islam yang Berkah</title>
        <meta name="description" content="Platform Ekosistem Ekonomi Islam, Marketplace UMKM Halal, Donasi Crowdfunding, Kelas Online (E-Course), dan Produk Digital Berkah di Barakah Economy." />
        <meta property="og:title" content="Barakah Economy - Ekosistem Ekonomi Syariah & Halal Marketplace" />
        <meta property="og:description" content="Platform Penguatan Sistem Ekonomi Islam yang Barakah: UMKM Halal, Donasi, Kelas Online, & Produk Digital." />
        <meta property="og:image" content="%PUBLIC_URL%/images/web-thumbnail.jpg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <link rel="canonical" href="https://barakah.cloud/" />

        {/* Schema.org WebSite Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Barakah Economy",
            "url": "https://barakah.cloud",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://barakah.cloud/store?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
      </Helmet>

      <HeaderHome onSearch={handleSearch} />

      {/* ============ HERO (Desktop Style brought to Mobile) ============ */}
      <section className="w-full bg-white hero-section py-6 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Banner Slider - Only Image Carousel on Mobile as requested */}
          <div className={`w-full transition-all duration-700 ${isFullscreen ? 'fixed inset-0 z-[1000] !w-full !h-full bg-black flex items-center justify-center p-0' : ''}`}>
            <div className={`relative group w-full ${isFullscreen ? 'max-w-7xl mx-auto' : ''}`}>
              {!isFullscreen && (
                <div className="absolute -inset-4 bg-gradient-to-tr from-green-300 to-blue-300 rounded-[2.5rem] blur-2xl opacity-20 transition duration-500"></div>
              )}
              <div className={`relative bg-white ${isFullscreen ? 'p-0 rounded-none' : 'p-1 rounded-[1.8rem]'} shadow-2xl border border-white/10 overflow-hidden`}>
                <div className={`relative ${isFullscreen ? 'h-screen w-screen' : 'aspect-video'} w-full overflow-hidden ${isFullscreen ? 'rounded-none' : 'rounded-[1.6rem]'}`}>
                  <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    pagination={{ clickable: true }}
                    autoplay={{ delay: 5000, disableOnInteraction: false }}
                    onSwiper={setSwiperInstance}
                    onSlideChange={handleSlideChange}
                    loop={heroBanners.length > 1}
                    className="w-full h-full"
                  >
                    {heroBanners.length > 0 ? (
                      heroBanners.map((banner) => (
                        <SwiperSlide key={banner.id}>
                          <div className="relative w-full h-full group/slide overflow-hidden cursor-pointer">
                            {banner.video ? (
                              <video
                                src={getMediaUrl(banner.video)}
                                className="w-full h-full object-cover hero-video"
                                autoPlay
                                muted
                                playsInline
                                loop={heroBanners.length === 1}
                                onPlay={() => swiperInstance?.autoplay.stop()}
                                onEnded={() => {
                                  if (heroBanners.length > 1) {
                                    swiperInstance?.slideNext();
                                    swiperInstance?.autoplay.start();
                                  }
                                }}
                                onClick={() => banner.target_url && window.open(banner.target_url, '_blank')}
                              />
                            ) : (
                              <img
                                src={getMediaUrl(banner.image)}
                                alt={banner.title}
                                className="w-full h-full object-cover"
                                onClick={() => banner.target_url && window.open(banner.target_url, '_blank')}
                              />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>
                          </div>
                        </SwiperSlide>
                      ))
                    ) : (
                      /* Fallback to dynamic logic if no hero banners */
                      [
                        activities[0] && { type: 'Kegiatan', title: activities[0].title, img: activities[0].header_image, link: `/kegiatan/${activities[0].id}` },
                        events.find(e => e.visibility === 'public') && {
                          type: 'Event',
                          title: events.find(e => e.visibility === 'public').title,
                          img: events.find(e => e.visibility === 'public').thumbnail || events.find(e => e.visibility === 'public').header_image,
                          link: `/event/${events.find(e => e.visibility === 'public').slug}`
                        },
                        campaigns[0] && { type: 'Charity', title: campaigns[0].title, img: campaigns[0].thumbnail, link: `/kampanye/${campaigns[0].slug || campaigns[0].id}` },
                        courses[0] && { type: 'Academy', title: courses[0].title, img: courses[0].thumbnail, link: `/kelas/${courses[0].slug || courses[0].id}` },
                        products[0] && { type: 'E-commerce', title: products[0].title, img: products[0].thumbnail, link: `/produk/${products[0].slug || products[0].id}` },
                      ].filter(Boolean).slice(0, 5).map((item, idx) => (
                        <SwiperSlide key={idx}>
                          <div className="relative w-full h-full group/slide cursor-pointer" onClick={() => navigate(item.link)}>
                            <img
                              src={getMediaUrl(item.img)}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                          </div>
                        </SwiperSlide>
                      ))
                    )}
                  </Swiper>
                  {/* Fullscreen Toggle */}
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className="absolute top-3 right-3 z-[10] w-8 h-8 bg-black/30 backdrop-blur-md border border-white/20 text-white rounded-full flex items-center justify-center transition-all"
                  >
                    <span className="material-icons text-sm">
                      {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Charity Slider & Highlight */}
      <div className="px-4 pt-4" style={{ position: 'relative', zIndex: 10 }}>
        {/* Section Title & Subtitle */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2">
          <div>
            <h1 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight">
              Bantu Saudaramu, Allah Bantu Kamu
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Sisihkan sebagian harta untuk program charity dan saudara yang membutuhkan
            </p>
          </div>
          <Link
            to="/charity"
            className="text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:underline self-start sm:self-auto flex items-center gap-0.5 mt-1 sm:mt-0"
          >
            <span>Lihat Semua</span>
            <span className="material-icons text-sm">arrow_forward</span>
          </Link>
        </div>



        {/* Highlight Card Banner */}
        {highlightCampaigns.length > 0 && (
          <div className="relative rounded-2xl overflow-hidden h-60 sm:h-64 shadow-md border border-gray-100 dark:border-gray-800 group">
            {/* Slides */}
            <div className="h-full relative">
              {highlightCampaigns.map((campaign, index) => {
                const isExpired = isCampaignExpired(campaign.deadline);
                const percent = campaign.current_amount && campaign.target_amount
                  ? Math.min(Math.round((campaign.current_amount / campaign.target_amount) * 100), 100)
                  : 0;

                return (
                  <div
                    key={campaign.id}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                      index === activeSlideCampaign ? 'opacity-100 z-10 pointer-events-auto' : 'opacity-0 z-0 pointer-events-none'
                    }`}
                  >
                    <img
                      src={getMediaUrl(campaign.thumbnail) || '/images/peduli-dhuafa-banner.jpg'}
                      alt={campaign.title}
                      className="w-full h-full object-cover transform scale-100 group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => {
                        e.target.src = '/images/peduli-dhuafa-banner.jpg';
                      }}
                    />

                    {/* Badges on Top */}
                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20 pointer-events-none">
                      <span className="px-2.5 py-1 bg-black/60 backdrop-blur-md text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-sm">
                        {campaign.is_featured ? '⭐ Highlight' : 'Charity'}
                      </span>
                      {campaign.deadline && (
                        <span className="px-2.5 py-1 bg-emerald-950/70 backdrop-blur-md text-emerald-200 text-[10px] font-bold rounded-lg shadow-sm flex items-center gap-1">
                          <span className="material-icons text-[12px]">schedule</span>
                          {formatDeadline(campaign.deadline)}
                        </span>
                      )}
                    </div>

                    {/* Bottom Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-4 z-20">
                      <Link to={`/kampanye/${campaign.slug || campaign.id}`} className="hover:underline">
                        <h2 className="text-white font-extrabold text-base sm:text-lg leading-tight line-clamp-2 mb-1.5 drop-shadow-md">
                          {campaign.title}
                        </h2>
                      </Link>

                      {/* Mini progress bar on highlight */}
                      <div className="w-full bg-white/20 rounded-full h-1.5 mb-2 overflow-hidden">
                        <div
                          className="bg-emerald-400 h-full rounded-full transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        />
                      </div>

                      {/* Amount info */}
                      <div className="flex items-center justify-between text-[11px] text-white/90 font-medium mb-3">
                        <span>
                          Terkumpul: <strong className="text-emerald-300 font-bold">{formatIDR(campaign.current_amount)}</strong>
                        </span>
                        <span>
                          {campaign.target_amount > 0
                            ? `${percent}% dari ${formatIDRTarget(campaign.target_amount)}`
                            : 'Target ∞'}
                        </span>
                      </div>

                      {/* Donate Button & Share */}
                      <div className="flex gap-2 items-center w-full">
                        <div className="flex-1 min-w-0">
                          {isExpired ? (
                            <button
                              className="w-full bg-gray-500/80 text-white py-2.5 px-4 rounded-xl text-xs font-bold cursor-not-allowed"
                              disabled
                            >
                              Waktu Donasi Selesai
                            </button>
                          ) : (
                            <Link
                              to={`/bayar-donasi/${campaign.slug || campaign.id}`}
                              className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 active:scale-95 text-white py-2.5 px-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-center block transition shadow-md shadow-emerald-950/40 truncate"
                            >
                              {getButtonLabel(campaign.title)}
                            </Link>
                          )}
                        </div>
                        <ShareButton
                          slug={campaign.slug || campaign.id}
                          title={campaign.title}
                          type="campaign"
                          description={campaign.description}
                          className="!w-10 !h-10 !rounded-xl !bg-white/90 !text-emerald-800 hover:!bg-white shrink-0 shadow-md"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicators */}
            {highlightCampaigns.length > 1 && (
              <div className="absolute bottom-2 right-2 flex space-x-1.5 z-30 bg-black/40 backdrop-blur-xs px-2 py-1 rounded-full">
                {highlightCampaigns.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlideCampaign(index)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      index === activeSlideCampaign ? 'w-5 bg-emerald-400' : 'w-1.5 bg-white/60 hover:bg-white'
                    }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Charity Swiper (List Kampanye) */}
      <div className="px-4 py-3">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="charity-swiper-custom relative pb-7">
            <style>{`
              .charity-swiper-custom .swiper-pagination {
                bottom: 0px !important;
              }
              .charity-swiper-custom .swiper-pagination-bullet {
                width: 6px;
                height: 6px;
                background: #cbd5e1;
                opacity: 0.8;
                margin: 0 3px !important;
                transition: all 0.3s ease;
              }
              .charity-swiper-custom .swiper-pagination-bullet-active {
                width: 18px;
                border-radius: 9999px;
                background: #059669;
                opacity: 1;
              }
              .charity-swiper-custom .swiper-button-next,
              .charity-swiper-custom .swiper-button-prev {
                display: none !important;
              }
            `}</style>
            <Swiper
              spaceBetween={12}
              slidesPerView={2}
              pagination={{ clickable: true, dynamicBullets: true }}
              modules={[Pagination]}
            >
              {sortedCampaigns.map((campaign) => {
                const isExpired = isCampaignExpired(campaign.deadline);
                const deadlineText = formatDeadline(campaign.deadline);
                const percent = campaign.current_amount && campaign.target_amount
                  ? Math.min(Math.round((campaign.current_amount / campaign.target_amount) * 100), 100)
                  : 0;

                return (
                  <SwiperSlide key={campaign.id} className="h-auto">
                    <div className="h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between overflow-hidden">
                      {/* Image & Category */}
                      <Link to={`/kampanye/${campaign.slug || campaign.id}`} className="relative block h-28 sm:h-32 w-full overflow-hidden bg-gray-100 dark:bg-gray-800 group">
                        <img
                          src={getMediaUrl(campaign.thumbnail) || '/placeholder-image.jpg'}
                          alt={campaign.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                        {campaign.category && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 backdrop-blur-xs text-white text-[9px] font-bold rounded-md uppercase tracking-wider">
                            {campaign.category}
                          </span>
                        )}
                      </Link>

                      {/* Content Body */}
                      <div className="p-2.5 sm:p-3 flex-1 flex flex-col justify-between">
                        <div>
                          <Link to={`/kampanye/${campaign.slug || campaign.id}`}>
                            <h3 className="text-xs sm:text-sm font-bold text-gray-900 dark:text-gray-100 line-clamp-2 h-8 leading-snug mb-1 hover:text-emerald-600 transition-colors">
                              {campaign.title}
                            </h3>
                          </Link>

                          <p className={`text-[10px] flex items-center gap-1 mb-1.5 ${isExpired ? 'text-red-500' : 'text-gray-400'}`}>
                            <span className="material-icons text-[11px]">{isExpired ? 'event_busy' : 'schedule'}</span>
                            <span className="truncate">{isExpired ? 'Waktu habis' : `Batas: ${deadlineText}`}</span>
                          </p>

                          {/* Progress Bar */}
                          <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mb-1.5 overflow-hidden">
                            <div
                              className="bg-emerald-600 h-full rounded-full transition-all duration-300"
                              style={{ width: `${percent}%` }}
                            />
                          </div>

                          {/* Progress Amount & Target */}
                          <div className="flex justify-between items-baseline gap-1 text-[11px] leading-tight mb-0.5">
                            <span className="font-extrabold text-emerald-700 dark:text-emerald-400 truncate">
                              {formatIDR(campaign.current_amount)}
                            </span>
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {campaign.target_amount > 0 ? formatIDRTarget(campaign.target_amount) : '∞'}
                            </span>
                          </div>

                          {/* Percent & Likes */}
                          <div className="flex justify-between items-center text-[10px] text-gray-500 mb-2">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-500">
                              {percent}% tercapai
                            </span>
                            <span className="flex items-center gap-0.5 text-red-500 font-bold">
                              <span className="material-icons text-[11px]">favorite</span>
                              {campaign.likes_count || 0}
                            </span>
                          </div>
                        </div>

                        {/* Action Buttons Row */}
                        <div className="flex gap-1.5 items-center w-full mt-auto pt-1">
                          <div className="flex-1 min-w-0">
                            {isExpired ? (
                              <button
                                disabled
                                className="w-full bg-gray-200 dark:bg-gray-800 text-gray-400 py-1.5 px-1 rounded-xl text-[11px] font-bold cursor-not-allowed text-center truncate"
                              >
                                Selesai
                              </button>
                            ) : (
                              <Link
                                to={`/bayar-donasi/${campaign.slug || campaign.id}`}
                                className="w-full bg-gradient-to-r from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800 active:scale-95 text-white py-1.5 px-2 rounded-xl text-[11px] font-black uppercase tracking-wider text-center block truncate transition shadow-xs"
                                title={getButtonLabel(campaign.title, false)}
                              >
                                {getButtonLabel(campaign.title, false)}
                              </Link>
                            )}
                          </div>
                          <ShareButton
                            slug={campaign.slug || campaign.id}
                            title={campaign.title}
                            type="campaign"
                            description={campaign.description}
                            className="!w-7 !h-7 !p-1 !rounded-xl !border-gray-200 text-gray-500 hover:text-emerald-600 shrink-0"
                          />
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-red-500">
            {error}
            <button
              onClick={() => fetchCampaigns(searchQuery)}
              className="ml-4 px-4 py-2 bg-green-500 text-white rounded-lg"
            >
              Coba Lagi
            </button>
          </div>
        )}
      </div>

      {/* Barakah Events Carousel */}
      {events.length > 0 && !searchQuery && (
        <div className="px-4 pt-4">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-lg font-medium line-clamp-2">Barakah Event</h1>
              <h2 className="text-sm font-medium text-gray-500">Ikuti berbagai kegiatan seru dari BAE</h2>
            </div>
            <Link to="/event" className="text-green-700 text-xs font-semibold hover:underline whitespace-nowrap">
              Lihat Semua →
            </Link>
          </div>
          <Swiper
            spaceBetween={12}
            slidesPerView={1.5}
            navigation
            modules={[Navigation, Autoplay]}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
          >
            {events.filter(e => e.visibility === 'public').slice(0, 10).map((event) => {
              const { isFinished } = getEventStatus(event.start_date, event.end_date);
              return (
                <SwiperSlide key={event.id}>
                  <Link to={`/event/${event.slug || event.id}`} className="block group">
                    <div className="relative aspect-[4/5] rounded-xl overflow-hidden shadow-sm border border-gray-100">
                      <img
                        src={getMediaUrl(event.thumbnail || event.header_image) || '/placeholder-image.jpg'}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://placehold.co/600x400?text=Barakah+Event'; }}
                      />
                      {/* Finished Overlay */}
                      {isFinished && (
                        <div className="absolute inset-0 bg-gray-900/60 flex items-center justify-center">
                          <span className="text-white font-bold text-xs uppercase tracking-widest border border-white/50 px-2 py-0.5 rounded rotate-[-10deg]">Selesai</span>
                        </div>
                      )}
                      {/* Date Badge */}
                      <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                        <p className="text-[10px] font-bold text-green-700">
                          {new Date(event.start_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                      {/* Live Badge */}
                      {event.active_stream && event.active_stream.is_live && (
                        <div className="absolute top-2 right-2 animate-pulse z-10">
                          <span className="bg-red-600 text-white text-[9px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest shadow-md flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                            LIVE
                          </span>
                        </div>
                      )}
                      {/* Bottom Gradient Overlay with Title & Description */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-10">
                        <h3 className="text-white font-bold text-sm leading-tight line-clamp-1 mb-0.5">{event.title}</h3>
                        <div className="flex items-center justify-between">
                          <p className="text-white/70 text-[10px] line-clamp-2 leading-tight">
                            {event.short_description || event.description?.replace(/<[^>]*>?/gm, '').substring(0, 80)}
                          </p>
                          <div className="flex items-center gap-1 opacity-80 ml-2">
                            <span className="material-icons text-[12px] text-red-400">favorite</span>
                            <span className="text-[10px] text-white font-bold">{event.likes_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}

      {/* BAE RUN Promotion Banner */}
      {/* BAE RUN SECTION - HIDE FOR NOW AS NOT FINISHED 
      <div className="px-4 py-8">
        <div className="relative bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl overflow-hidden shadow-2xl border border-white/5">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 blur-3xl rounded-full translate-x-10 -translate-y-10"></div>
          <div className="p-6 flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-500/20 border border-green-500/20 rounded-full mb-3">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-[10px] font-black uppercase tracking-widest text-green-400">Barakah Activity</span>
              </div>
              <h2 className="text-2xl font-black text-white tracking-tighter italic uppercase leading-none mb-2">
                BAE <span className="text-green-500">RUN</span>
              </h2>
              <p className="text-xs text-slate-400 leading-tight mb-4 max-w-[200px]">
                Mulai hidup sehat & berkah. Lacak lari Anda dan kumpulkan poin keberkahan.
              </p>
              <Link 
                to="/bae-run" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition shadow-lg shadow-green-900/20"
              >
                Cek Aplikasi 
                <span className="material-icons text-[14px]">arrow_forward</span>
              </Link>
            </div>
            <div className="relative w-28 h-40 transform rotate-6 translate-y-4">
              <div className="absolute inset-0 bg-slate-950 rounded-2xl border-4 border-slate-700 shadow-xl overflow-hidden">
                <img 
                  src="/media/bae_run_app_mockup_1777304049543.png" 
                  alt="BAE RUN Mockup" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Activities Carousel */}
      {activities.length > 0 && (
        <div className="px-4 pt-4">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-lg font-medium line-clamp-2">Kegiatan Kami</h1>
              <h2 className="text-sm font-medium text-gray-500">Program dan aktivitas komunitas BAE</h2>
            </div>
            <Link to="/kegiatan" className="text-green-700 text-xs font-semibold hover:underline whitespace-nowrap">
              Lihat Semua →
            </Link>
          </div>
          <Swiper
            spaceBetween={12}
            slidesPerView={1.5}
            navigation
            modules={[Navigation, Autoplay]}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
          >
            {activities.map((act) => (
              <SwiperSlide key={act.id}>
                <Link to={`/kegiatan/${act.id}`} className="block bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                  <div className="relative h-32">
                    <img
                      src={getMediaUrl(act.header_image)}
                      alt={act.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = '/placeholder-image.jpg'; }}
                    />
                    <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      <p className="text-[10px] font-bold text-green-700">
                        {new Date(act.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-gray-900 line-clamp-2">{act.title}</h3>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Product Slider */}
      <div className="px-4 pt-4" style={{ position: 'relative', zIndex: 10 }}>
        <h1 className="text-lg font-medium mb-2 line-clamp-2">Penuhi kebutuhan harianmu</h1>
        <h2 className="text-sm font-medium mb-2 line-clamp-2">Beli produk Halal, Toyyib dan Barakah disini</h2>
        <div className="mb-3 mt-4 bg-yellow-50 p-3 rounded-lg text-sm border border-yellow-200">
          <p className="text-yellow-800">
            <strong>Catatan:</strong> Fitur ini hanya untuk kalangan terbatas, anggota Barakah Economy
          </p>
        </div>
        {featuredProducts.length > 0 && (
          <div className="relative rounded-lg overflow-hidden h-56">
            {/* Slides */}
            <div className="h-full">
              {featuredProducts.map((product, index) => {
                const effectiveStock = getProductStock(product);
                return (
                  <div
                    key={product.id}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-500 ${index === activeSlideProduct ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                  >
                    <img
                      src={product.thumbnail || '/images/peduli-dhuafa-banner.jpg'}
                      alt={product.title}
                      className="w-full h-56 object-cover"
                      onError={(e) => {
                        e.target.src = '/images/peduli-dhuafa-banner.jpg';
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h2 className="text-white font-bold text-lg">{product.title}</h2>
                      <h2 className="text-white text-sm">stok{' '} {effectiveStock > 0 ? effectiveStock : 'habis'}</h2>
                      {effectiveStock <= 0 ? (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => addToCart(product.id)}
                            className="w-full block text-center bg-gray-400 text-white py-2 rounded-md text-sm hover:bg-gray-500 flex items-center justify-center cursor-not-allowed"
                            disabled
                          >
                            <span className="material-icons text-sm">block</span>Stok Habis
                          </button>
                        </div>

                      ) : (
                        <div className="flex flex-col gap-2 w-full mt-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => addToCart(product.id)}
                              className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-green-100 transition-all transform hover:-translate-y-1 text-xs"
                            >
                              <span className="material-icons text-sm">shopping_cart</span>
                              + Keranjang
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              addToCart(product.id);
                              setTimeout(() => {
                                const bubble = document.getElementById('cart-floating-bubble');
                                if (bubble) bubble.click();
                              }, 500);
                            }}
                            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-emerald-100 transition-all transform hover:-translate-y-1 text-xs"
                          >
                            <span className="material-icons text-sm">shopping_bag</span>
                            Beli Langsung
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicators */}
            {featuredProducts.length > 0 && (
              <div className="absolute bottom-2 right-2 flex space-x-2 z-20">
                {featuredProducts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlideProduct(index)}
                    className={`w-2 h-2 rounded-full ${index === activeSlideProduct ? 'bg-white' : 'bg-white/50'
                      }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Store Products Carousel Section */}
      <div className="px-4 py-4 bg-gray-50/70 mt-2 border-y border-gray-100 overflow-hidden">
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="material-icons text-emerald-600 text-lg">storefront</span>
              <h2 className="text-sm font-bold text-gray-900">
                {i18n?.language === 'en' ? 'UMKM Store Barakah' : 'Toko UMKM Barakah'}
              </h2>
            </div>
            <p className="text-[11px] text-gray-500">
              {i18n?.language === 'en' ? 'Curated halal & premium quality products' : 'Produk halal & berkualitas pilihan'}
            </p>
          </div>
          <Link 
            to="/store" 
            className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100 shadow-sm"
          >
            <span>{i18n?.language === 'en' ? 'All' : 'Semua'}</span>
            <span className="material-icons text-xs">chevron_right</span>
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <Swiper
            spaceBetween={10}
            slidesPerView={2.2}
            className="w-full overflow-hidden"
          >
            {sortedProducts.map((product) => {
              const effectiveStock = getProductStock(product);
              const basePrice = Number(product.price || 0);

              // Variations & Price Range
              let minP = product.min_price !== undefined && product.min_price !== null ? Number(product.min_price) : null;
              let maxP = product.max_price !== undefined && product.max_price !== null ? Number(product.max_price) : null;
              if (product.variations && Array.isArray(product.variations) && product.variations.length > 0) {
                const varPrices = product.variations
                  .filter(v => v && (v.is_active === undefined || v.is_active === true) && (v.name || Number(v.additional_price) > 0))
                  .map(v => Number(v.additional_price) > 0 ? Number(v.additional_price) : basePrice)
                  .filter(p => !isNaN(p) && p > 0);
                if (varPrices.length > 0) {
                  const calculatedMin = Math.min(...varPrices);
                  const calculatedMax = Math.max(...varPrices);
                  minP = minP !== null ? Math.min(minP, calculatedMin) : calculatedMin;
                  maxP = maxP !== null ? Math.max(maxP, calculatedMax) : calculatedMax;
                }
              }
              if (minP === null) minP = basePrice;
              if (maxP === null) maxP = basePrice;
              const hasRange = minP < maxP;

              let finalPrice = basePrice;
              let finalMinP = minP;
              let finalMaxP = maxP;
              let hasPromo = false;
              let promoDiscountPct = 0;

              if (product.active_promotion) {
                const promo = product.active_promotion;
                if (promo.discount_type === 'percentage') {
                  promoDiscountPct = Math.min(100, Math.max(1, Math.round(Number(promo.discount_value || 0))));
                  finalPrice = Math.max(0, basePrice - (basePrice * (promoDiscountPct / 100)));
                  finalMinP = Math.max(0, Math.round(minP * (1 - promoDiscountPct / 100)));
                  finalMaxP = Math.max(0, Math.round(maxP * (1 - promoDiscountPct / 100)));
                  hasPromo = true;
                } else if (promo.discount_type === 'nominal') {
                  const discVal = Number(promo.discount_value || 0);
                  finalPrice = Math.max(0, basePrice - discVal);
                  finalMinP = Math.max(0, minP - discVal);
                  finalMaxP = Math.max(0, maxP - discVal);
                  promoDiscountPct = basePrice > 0 ? Math.min(100, Math.max(1, Math.round((discVal / basePrice) * 100))) : 0;
                  hasPromo = true;
                }
              }

              return (
                <SwiperSlide key={product.id} className="!h-auto flex">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col justify-between w-full hover:shadow-md transition-all">
                    <div>
                      <Link to={`/produk/${product.slug || product.id}`} className="block relative aspect-square overflow-hidden bg-gray-50">
                        <img
                          src={product.thumbnail || '/placeholder-image.jpg'}
                          alt={product.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                        {hasPromo && (
                          <span className="absolute top-1.5 left-1.5 bg-gradient-to-r from-red-600 to-rose-600 text-white text-[8px] font-black px-1.5 py-0.5 rounded-md shadow-sm">
                            {promoDiscountPct > 0 ? `-${promoDiscountPct}%` : 'PROMO'}
                          </span>
                        )}
                      </Link>
                      <div className="p-2.5 pb-1">
                        <Link to={`/produk/${product.slug || product.id}`}>
                          <h3 className="text-xs font-bold text-gray-800 line-clamp-2 hover:text-emerald-700 transition mb-1 h-7 leading-tight">
                            {product.title}
                          </h3>
                        </Link>
                        
                        <div className="mb-1">
                          {hasPromo ? (
                            <div className="flex flex-col">
                              <span className="text-xs font-extrabold text-emerald-700">
                                {hasRange ? `${formatIDR(finalMinP)} - ${formatIDR(finalMaxP)}` : formatIDR(finalPrice)}
                              </span>
                              <span className="text-[9px] text-gray-400 line-through">
                                {hasRange ? `${formatIDR(minP)} - ${formatIDR(maxP)}` : formatIDR(basePrice)}
                              </span>
                            </div>
                          ) : (
                            <span className="text-xs font-extrabold text-emerald-700">
                              {hasRange ? `${formatIDR(finalMinP)} - ${formatIDR(finalMaxP)}` : formatIDR(basePrice)}
                            </span>
                          )}
                          <div className="flex justify-between items-center text-[9px] text-gray-400 mt-0.5">
                            <span>Stok: <b className={effectiveStock > 0 ? 'text-gray-700' : 'text-red-500'}>{effectiveStock > 0 ? effectiveStock : '0'}</b></span>
                            <div className="flex items-center gap-1.5">
                              {product.seller_show_sold_count !== false && (
                                <span className="font-semibold text-emerald-700">
                                  {product.sold_count || 0} terjual
                                </span>
                              )}
                              <div className="flex items-center gap-0.5">
                                <span className="material-icons text-[10px] text-rose-500">favorite</span>
                                <span>{product.likes_count || 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-2.5 pt-0 mt-auto">
                      {effectiveStock <= 0 ? (
                        <button
                          className="w-full bg-gray-100 text-gray-400 py-1.5 rounded-xl flex items-center justify-center gap-1 cursor-not-allowed text-[9px] font-bold"
                          disabled
                        >
                          <span className="material-icons text-xs">remove_shopping_cart</span>
                          Habis
                        </button>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <button
                              onClick={() => addToCart(product.id)}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 rounded-xl font-bold flex items-center justify-center gap-1 shadow-sm transition text-[9px]"
                            >
                              <span className="material-icons text-xs">shopping_cart</span>
                              + Keranjang
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              addToCart(product.id);
                              setTimeout(() => {
                                const bubble = document.getElementById('cart-floating-bubble');
                                if (bubble) bubble.click();
                              }, 500);
                            }}
                            className="w-full bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 py-1 rounded-xl font-bold flex items-center justify-center gap-1 transition text-[9px]"
                          >
                            <span className="material-icons text-xs">shopping_bag</span>
                            Beli Langsung
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        )}

        {error && (
          <div className="text-center py-4 text-red-500 text-xs">
            {error}
            <button
              onClick={() => fetchProducts(searchQuery)}
              className="ml-2 px-3 py-1 bg-emerald-600 text-white rounded-lg text-xs"
            >
              Coba Lagi
            </button>
          </div>
        )}
      </div>

      {/* Course Slider */}
      <div className="px-4 pt-4" style={{ position: 'relative', zIndex: 10 }}>
        <h1 className="text-lg font-medium mb-2 line-clamp-2">Menuntut ilmu wajib bagi setiap muslim</h1>
        <h2 className="text-sm font-medium mb-2 line-clamp-2">Tingkatkan wawasan, tambah ilmu dan keterampilan disini</h2>
        <div className="mb-3 mt-4 bg-yellow-50 p-3 rounded-lg text-sm border border-yellow-200">
          <p className="text-yellow-800">
            <strong>Catatan:</strong> Fitur ini hanya untuk kalangan terbatas, anggota Barakah Economy
          </p>
        </div>
        {featuredCourses.length > 0 && (
          <div className="relative rounded-lg overflow-hidden h-56">
            {/* Slides */}
            <div className="h-full">
              {featuredCourses.map((course, index) => {
                return (
                  <div
                    key={course.id}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-500 ${index === activeSlideCourse ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                  >
                    <img
                      src={course.thumbnail || '/images/peduli-dhuafa-banner.jpg'}
                      alt={course.title}
                      className="w-full h-56 object-cover"
                      onError={(e) => {
                        e.target.src = '/images/peduli-dhuafa-banner.jpg';
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h2 className="text-white font-bold text-lg">{course.title}</h2>
                      <Link
                        to={`/kelas/${course.slug || course.id}`}
                        className="block text-center bg-green-800 text-white py-2 rounded-md text-sm hover:bg-green-900"
                      >
                        LIHAT KELAS
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicators */}
            {featuredCourses.length > 0 && (
              <div className="absolute bottom-2 right-2 flex space-x-2 z-20">
                {featuredCourses.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlideCourse(index)}
                    className={`w-2 h-2 rounded-full ${index === activeSlideCourse ? 'bg-white' : 'bg-white/50'
                      }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Course Swiper */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        ) : (
          <div className="swiper-container">
            <Swiper
              spaceBetween={16}
              slidesPerView={2}
              navigation
              pagination={{ clickable: true }}
              scrollbar={{ draggable: true }}
              modules={[Navigation, Pagination, Scrollbar]}
            >
              {sortedCourses.map((course) => {
                return (
                  <SwiperSlide key={course.id}>
                    <div className="bg-white rounded-lg overflow-hidden shadow">
                      <Link to={`/kelas/${course.slug || course.id}`}>
                        <img
                          src={course.thumbnail || '/placeholder-image.jpg'}
                          alt={course.title}
                          className="w-full h-28 object-cover"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </Link>
                      <div className="p-2">
                        <h3 className="text-sm font-medium mb-2 line-clamp-2">
                          {course.title}
                        </h3>
                        <p className="text-gray-600 text-xs mb-2 flex justify-between items-center">
                          {formatIDRCourse(course.price)}
                          <span className="flex items-center gap-1 opacity-60">
                            <span className="material-icons text-[12px] text-red-500">favorite</span>
                            <span className="text-[10px] font-bold">{course.likes_count || 0}</span>
                          </span>
                        </p>
                        <div className="flex gap-2 items-center w-full mt-2">
                          <div className="flex-1">
                            <Link
                              to={`/kelas/${course.slug || course.id}`}
                              className="block text-center bg-green-800 text-white py-2 rounded-md text-sm hover:bg-green-900"
                            >
                              LIHAT KELAS
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}

        {error && (
          <div className="text-center py-4 text-red-500">
            {error}
            <button
              onClick={() => fetchCourses(searchQuery)}
              className="ml-4 px-4 py-2 bg-green-500 text-white rounded-lg"
            >
              Coba Lagi
            </button>
          </div>
        )}
      </div>

      {/* Digital Product Slider */}
      <div className="px-4 pt-4" style={{ position: 'relative', zIndex: 10 }}>
        <h1 className="text-lg font-medium mb-2 line-clamp-2">Produk Digital BAE</h1>
        <h2 className="text-sm font-medium mb-2 line-clamp-2">Akses ilmu dan kemudahan dengan produk digital berkualitas</h2>

        {featuredDigitalProducts.length > 0 && (
          <div className="relative rounded-lg overflow-hidden h-56">
            {/* Slides */}
            <div className="h-full">
              {featuredDigitalProducts.map((product, index) => {
                return (
                  <div
                    key={product.id}
                    className={`absolute top-0 left-0 w-full h-full transition-opacity duration-500 ${index === activeSlideDigital ? 'opacity-100 z-10' : 'opacity-0 z-0'
                      }`}
                  >
                    <img
                      src={product.thumbnail || '/placeholder-image.jpg'}
                      alt={product.title}
                      className="w-full h-56 object-cover"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                      <h2 className="text-white font-bold text-lg">{product.title}</h2>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-white font-bold">{formatIDR(product.price)}</span>
                        <Link
                          to={`/digital-products/${product.slug}`}
                          className="bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold"
                        >
                          BELI SEKARANG
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Indicators */}
            {featuredDigitalProducts.length > 0 && (
              <div className="absolute bottom-2 right-2 flex space-x-2 z-20">
                {featuredDigitalProducts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlideDigital(index)}
                    className={`w-2 h-2 rounded-full ${index === activeSlideDigital ? 'bg-white' : 'bg-white/50'
                      }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        ) : (
          <div className="swiper-container">
            <Swiper
              spaceBetween={16}
              slidesPerView={2}
              navigation
              pagination={{ clickable: true }}
              scrollbar={{ draggable: true }}
              modules={[Navigation, Pagination, Scrollbar]}
            >
              {digitalProducts.map((product) => {
                return (
                  <SwiperSlide key={product.id}>
                    <div className="bg-white rounded-lg overflow-hidden shadow">
                      <Link to={`/digital-products/${product.slug}`}>
                        <img
                          src={product.thumbnail || '/placeholder-image.jpg'}
                          alt={product.title}
                          className="w-full h-28 object-cover"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </Link>
                      <div className="p-2">
                        <h3 className="text-sm font-medium mb-1 line-clamp-1">{product.title}</h3>
                        <p className="text-green-700 font-bold text-xs mb-2">{formatIDR(product.price)}</p>
                        <Link
                          to={`/digital-products/${product.slug}`}
                          className="block text-center bg-green-800 text-white py-2 rounded-md text-xs hover:bg-green-900"
                        >
                          DETAIL PRODUK
                        </Link>
                      </div>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}
      </div>

      {/* Popular Sellers Section */}
      <div className="px-4 py-4 mb-4">
        <h1 className="text-lg font-medium mb-4">Penjual Populer</h1>
        <div className="flex overflow-x-auto space-x-4 pb-4 scrollbar-hide">
          {popularSellers.map((seller) => (
            <Link
              key={seller.username}
              to={`/store/${seller.username}`}
              className="flex-shrink-0 w-28 text-center"
            >
              <div className="w-20 h-20 mx-auto rounded-full overflow-hidden border-2 border-green-500 p-0.5 mb-2">
                <img
                  src={getMediaUrl(seller.picture || seller.shop_thumbnail) || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(seller.name) + '&background=0D8ABC&color=fff'}
                  alt={seller.name}
                  className="w-full h-full object-cover rounded-full"
                  onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(seller.name) + '&background=0D8ABC&color=fff'; }}
                />
              </div>
              <p className="text-xs font-bold text-gray-800 truncate px-2">@{seller.username}</p>
              <p className="text-[10px] text-gray-500 truncate px-2">{seller.name}</p>
            </Link>
          ))}
          {popularSellers.length === 0 && !loading && (
            <p className="text-sm text-gray-500 italic">Belum ada penjual aktif</p>
          )}
        </div>
      </div>

      {/* Partner Section */}
      {onlyPartners.length > 0 && (
        <div className="mb-16 px-4 py-12 bg-white rounded-[3rem] border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-green-50 rounded-full blur-3xl opacity-30 -mr-32 -mt-32"></div>
          <h2 className="text-2xl font-extrabold mb-8 text-center text-gray-900 flex items-center justify-center gap-3">
            <span className="w-8 h-1 bg-green-600 rounded-full"></span>
            Mitra Kebaikan Barakah
            <span className="w-8 h-1 bg-green-600 rounded-full"></span>
          </h2>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={3}
            centeredSlides={true}
            loop={true}
            autoplay={{ delay: 3000 }}
            breakpoints={{
              640: { slidesPerView: 4 },
              1024: { slidesPerView: 7 },
            }}
          >
            {onlyPartners.map(partner => (
              <SwiperSlide key={partner.id}>
                <div
                  onClick={() => setSelectedPartner(partner)}
                  className="flex flex-col items-center justify-center p-4 hover:scale-105 transition duration-300 cursor-pointer group text-center"
                >
                  <div className="h-16 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <img src={getMediaUrl(partner.logo)} alt={partner.name} className="h-full object-contain" title={partner.link ? 'Klik untuk buka link' : 'Klik untuk lihat detail'} />
                  </div>
                  <span className="text-[10px] font-bold text-gray-400 group-hover:text-green-600 transition-colors uppercase tracking-widest text-center w-full block">{partner.name}</span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Mitra Section */}
      {onlyMitra.length > 0 && (
        <div className="mb-16 px-4 py-12 bg-gray-50 rounded-[3rem] border border-gray-100 shadow-inner relative overflow-hidden">
          <div className="absolute top-0 left-0 w-64 h-64 bg-green-50 rounded-full blur-3xl opacity-30 -ml-32 -mt-32"></div>
          <h2 className="text-2xl font-extrabold mb-8 text-center text-gray-900 flex items-center justify-center gap-3">
            <span className="w-8 h-1 bg-green-600 rounded-full"></span>
            Mitra Bisnis Barakah
            <span className="w-8 h-1 bg-green-600 rounded-full"></span>
          </h2>
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={3}
            centeredSlides={true}
            loop={true}
            autoplay={{ delay: 3500 }}
            breakpoints={{
              640: { slidesPerView: 4 },
              1024: { slidesPerView: 7 },
            }}
          >
            {onlyMitra.map(mitra => (
              <SwiperSlide key={mitra.id}>
                <div
                  onClick={() => setSelectedPartner(mitra)}
                  className="flex flex-col items-center justify-center p-4 hover:scale-105 transition duration-300 cursor-pointer group text-center"
                >
                  <div className="h-16 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                    <img src={getMediaUrl(mitra.logo)} alt={mitra.name} className="h-full object-contain" title={mitra.link ? 'Klik untuk buka link' : 'Klik untuk lihat detail'} />
                  </div>
                  <span className="text-[10px] font-bold text-gray-400 group-hover:text-green-600 transition-colors uppercase tracking-widest text-center w-full block">{mitra.name}</span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {/* Testimonial Section */}
      <div className="mb-12 bg-green-50 rounded-3xl p-8 border border-green-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-green-100 rounded-full -mr-16 -mt-16 opacity-50"></div>
        <div className="relative z-10">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold text-gray-800">Review & Testimoni</h2>
            <button
              onClick={() => setShowTestimonialModal(true)}
              className="text-xs font-bold text-green-700 bg-white px-4 py-2 rounded-full border border-green-200 shadow-sm hover:bg-green-600 hover:text-white transition"
            >
              {myTestimonial ? 'Ubah Testimoni' : 'Tulis Testimoni'}
            </button>
          </div>

          {testimonials.length === 0 ? (
            <p className="text-center text-gray-500 py-4 italic">Belum ada testimoni.</p>
          ) : (
            <Swiper
              modules={[Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2 },
              }}
              className="pb-10"
            >
              {testimonials.map(t => (
                <SwiperSlide key={t.id}>
                  <div className="bg-white p-6 rounded-2xl shadow-sm border border-green-50 h-full">
                    <div className="flex items-center gap-1 mb-3 text-orange-400">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="material-icons text-sm">{i < t.rating ? 'star' : 'star_border'}</span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 italic mb-4">"{t.content}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-700 text-xs font-bold">
                        {t.user_full_name?.[0] || 'U'}
                      </div>
                      <span className="text-xs font-bold text-gray-800">{t.user_full_name || 'Hamba Allah'}</span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </div>
      </div>

      {/* Testimonial Modal */}
      {showTestimonialModal && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl animate-slide-up">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold">{myTestimonial ? 'Ubah Testimoni Anda' : 'Tulis Testimoni Anda'}</h3>
              <button onClick={() => setShowTestimonialModal(false)} className="material-icons text-gray-400">close</button>
            </div>
            <form onSubmit={handleTestimonialSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })}
                      className={`material-icons ${star <= testimonialForm.rating ? 'text-orange-400' : 'text-gray-300'}`}
                    >
                      star
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Pesan Anda</label>
                <textarea
                  required
                  rows="4"
                  value={testimonialForm.content}
                  onChange={(e) => setTestimonialForm({ ...testimonialForm, content: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-100 border-none rounded-xl text-sm"
                  placeholder="Ceritakan pengalaman Anda menggunakan Barakah App..."
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-green-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-green-100"
              >
                {myTestimonial ? 'Simpan Perubahan' : 'Kirim Testimoni'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Partner/Mitra Detail Modal */}
      {selectedPartner && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl animate-fade-in">
            <div className="relative h-48 bg-gray-50 flex items-center justify-center">
              <button
                onClick={() => setSelectedPartner(null)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/80 rounded-full flex items-center justify-center text-gray-600 hover:bg-white transition z-10"
              >
                <span className="material-icons">close</span>
              </button>
              <img
                src={getMediaUrl(selectedPartner.logo)}
                alt={selectedPartner.name}
                className="max-h-32 max-w-[80%] object-contain"
              />
            </div>
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${selectedPartner.type === 'mitra' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                  {selectedPartner.type || 'Partner'}
                </span>
                <h3 className="text-2xl font-bold text-gray-900">{selectedPartner.name}</h3>
              </div>
              <div className="text-gray-600 leading-relaxed text-sm whitespace-pre-line mb-6">
                {selectedPartner.description || 'Belum ada deskripsi untuk partner ini.'}
              </div>
              {selectedPartner.link && (
                <a
                  href={selectedPartner.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-4 bg-green-700 text-white rounded-2xl text-sm font-bold shadow-lg shadow-green-100 hover:bg-green-800 transition mb-3"
                >
                  <span className="material-icons text-lg">public</span>
                  Lihat Profile Disini
                </a>
              )}
              <button
                onClick={() => setSelectedPartner(null)}
                className="w-full py-3 bg-gray-100 text-gray-600 rounded-2xl text-sm font-bold hover:bg-gray-200 transition"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kegiatan Komunitas */}
      {activities.length > 0 && (
        <div className="hidden md:block bg-gray-50 py-10 px-4">
          <div className="max-w-md mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">Kegiatan Terbaru</h2>
              <span className="text-green-700 text-xs font-bold">Lihat Semua</span>
            </div>
            <div className="space-y-4">
              {activities.map(act => (
                <div key={act.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex h-28">
                  <div className="w-1/3 h-full">
                    <img src={getMediaUrl(act.header_image)} alt={act.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="w-2/3 p-3 flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-gray-800 line-clamp-2">{act.title}</h3>
                      <div className="text-[10px] text-gray-500 mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: act.content }}></div>
                    </div>
                    <p className="text-[10px] text-green-700 font-bold">{new Date(act.date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Forum Tanya Jawab */}
      <div className="px-4 py-8 mb-4 border-t border-gray-100 dark:border-gray-800">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Forum Tanya Jawab</h1>
            <h2 className="text-xs text-gray-500 dark:text-gray-400 mt-1">Diskusikan topik bersama para pakar</h2>
          </div>
          <Link to="/forum" className="text-xs font-bold text-green-700 dark:text-green-400 flex items-center gap-0.5">
            Lihat Semua <span className="material-icons text-[14px]">arrow_forward</span>
          </Link>
        </div>

        {threads && threads.length > 0 ? (
          <div className="mb-6">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={16}
              slidesPerView={1.2}
              breakpoints={{
                640: { slidesPerView: 2.2 },
                768: { slidesPerView: 3.2 }
              }}
              autoplay={{ delay: 4000, disableOnInteraction: false }}
              className="py-2"
            >
              {threads.map(thread => (
                <SwiperSlide key={thread.id}>
                  <Link
                    to={`/forum/${thread.slug}`}
                    className="block bg-white dark:bg-[#111827] p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 hover:shadow-md transition h-48 flex flex-col justify-between"
                  >
                    <div>
                      {/* Author */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-700 dark:text-indigo-300 font-bold text-xs flex-shrink-0">
                          {thread.author?.full_name?.charAt(0) || 'U'}
                        </div>
                        <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 truncate max-w-[80px]">
                          {thread.author?.full_name}
                        </span>
                        {thread.author?.is_expert && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 scale-90">
                            Pakar
                          </span>
                        )}
                      </div>
                      {/* Title */}
                      <h3 className="text-xs font-bold text-gray-900 dark:text-white line-clamp-2 mb-1">
                        {thread.title}
                      </h3>
                      {/* Snippet */}
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-3">
                        {thread.content}
                      </p>
                    </div>

                    {/* Stats Footer */}
                    <div className="flex gap-3 text-[9px] text-gray-400 dark:text-gray-500 border-t border-gray-50 dark:border-gray-800/80 pt-2 mt-2">
                      <div className="flex items-center gap-0.5">
                        <span className="material-icons text-[12px]">favorite</span>
                        <span>{thread.likes_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <span className="material-icons text-[12px]">chat</span>
                        <span>{thread.replies_count || 0}</span>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className="bg-indigo-50 dark:bg-indigo-950/20 p-6 rounded-2xl flex flex-col items-center justify-center text-center border border-indigo-100 dark:border-indigo-900/50">
            <div className="w-16 h-16 bg-white dark:bg-[#111827] rounded-full flex items-center justify-center mb-4 shadow-sm">
              <span className="material-icons text-indigo-600 dark:text-indigo-400 text-3xl">forum</span>
            </div>
            <p className="text-xs text-gray-700 dark:text-gray-300 mb-6">
              Dapatkan jawaban atas pertanyaan seputar bisnis, fiqih, dan masalah keseharian dari para ahlinya.
            </p>
            <Link to="/forum" className="w-full max-w-[200px] py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold shadow-md hover:bg-indigo-700 transition">
              Buka Forum
            </Link>
          </div>
        )}
      </div>

      {/* Artikel & Edukasi Section */}
      {articles && articles.length > 0 && (
        <div className="px-4 py-8 mb-4 border-t border-gray-100 dark:border-gray-800">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-xl font-bold text-gray-800 dark:text-white">Artikel Terbaru</h1>
              <h2 className="text-xs text-gray-500 dark:text-gray-400 mt-1">Kembangkan wawasan bisnis dan ilmu syariah</h2>
            </div>
            <Link to="/academy/articles" className="text-xs font-bold text-green-700 dark:text-green-400 flex items-center gap-0.5">
              Lihat Semua <span className="material-icons text-[14px]">arrow_forward</span>
            </Link>
          </div>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={16}
            slidesPerView={1.2}
            breakpoints={{
              640: { slidesPerView: 2.2 },
              768: { slidesPerView: 3.2 }
            }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            className="py-2"
          >
            {articles.map(article => {
              const stripHtml = (html) => {
                if (!html) return "";
                const doc = new DOMParser().parseFromString(html, 'text/html');
                return doc.body.textContent || "";
              };
              const hasImage = article.images && article.images.length > 0;
              return (
                <SwiperSlide key={article.id}>
                  <Link
                    to={`/academy/articles/${article.slug || article.id}`}
                    className="block bg-white dark:bg-[#111827] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-md transition h-64 flex flex-col justify-between"
                  >
                    {/* Header Image */}
                    <div className="h-28 w-full relative bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                      {hasImage ? (
                        <img
                          src={article.images[0].full_path}
                          alt={article.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-600 text-xs">
                          No Image
                        </div>
                      )}
                      <span className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider">
                        {article.date}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-3 flex-grow flex flex-col justify-between">
                      <div>
                        <h3 className="text-xs font-bold text-gray-900 dark:text-white line-clamp-2 mb-1 leading-tight">
                          {article.title}
                        </h3>
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-3">
                          {stripHtml(article.content)}
                        </p>
                      </div>

                      {/* Stats */}
                      <div className="flex justify-between items-center text-[9px] text-gray-400 dark:text-gray-500 border-t border-gray-50 dark:border-gray-800/80 pt-2 mt-2">
                        <div className="flex items-center gap-1">
                          <span className="material-icons text-[12px]">visibility</span>
                          <span>{article.view_count || 0}</span>
                        </div>
                        <div className="flex items-center gap-1 text-red-400">
                          <span className="material-icons text-[12px]">favorite</span>
                          <span className="font-bold">{article.likes_count || 0}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      )}


      {/* Struktur Team Section */}
      {aboutUs?.organization_structure_image && (
        <section className="py-20 px-4 md:px-8 bg-[#fcfdfe] overflow-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 uppercase tracking-tight">Struktur Team</h2>
              <div className="w-24 h-1.5 bg-green-600 mx-auto rounded-full"></div>
            </div>

            <div className="flex justify-center">
              <div className="bg-white p-4 md:p-8 rounded-[2rem] md:rounded-[3.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 max-w-4xl">
                <img 
                  src={getMediaUrl(aboutUs.organization_structure_image)} 
                  alt="Struktur Team" 
                  className="w-full h-auto rounded-2xl md:rounded-[2.5rem] shadow-inner"
                  onClick={() => window.location.href = '/about?tab=team'}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Tentang Kami Section */}
      <section id="about" className="py-20 px-8 lg:px-24 bg-white">
        <div className="max-w-6xl mx-auto">
          {aboutUs?.hero_image && (
            <div className="w-full h-64 md:h-96 rounded-3xl overflow-hidden mb-12 shadow-lg">
              <img src={getMediaUrl(aboutUs.hero_image)} alt="Barakah Economy" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex flex-col md:flex-row items-start gap-12">
            <div className="flex-1 space-y-6">
              <h2 className="text-3xl font-bold text-gray-900">{aboutUs?.title || 'Tentang Kami'}</h2>
              <div className="w-20 h-1 bg-green-600 rounded-full"></div>
              {aboutUs?.description ? (
                <div className="text-gray-600 leading-relaxed whitespace-pre-line text-sm">
                  {aboutUs.description}
                </div>
              ) : (
                <>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    BAE Community berdiri pada tanggal 29 Februari 2024 di Jalan Tubagus Ismail Dalam No.19C dan bertempat di Dago, Kota Bandung, Jawa Barat. Tujuan BAE Community adalah meningkatkan kestabilan finansial masyarakat melalui pengembangan ekosistem ekonomi yang berlandaskan syariah Islam dengan memberdayakan pemuda dan mahasiswa sebagai pionir perubahan.
                  </p>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    BAE Community memiliki tugas pokok menyelenggarakan kegiatan yang bersifat pemberdayaan, pendidikan, kolaborasi, pengembangan serta sosial baik ke dalam yaitu internal komunitas maupun keluar yaitu lingkungan masyarakat.
                  </p>
                </>
              )}
            </div>
            <div className="flex-1 space-y-6">
              <div className="bg-green-50 p-6 rounded-2xl border border-green-100">
                <h3 className="text-lg font-bold text-green-800 mb-3">🎯 Visi</h3>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                  {aboutUs?.vision || 'Menjadi komunitas yang unggul dalam mengembangkan perekonomian berbasis syariah yang berkeadilan dan berkelanjutan, serta berkontribusi secara aktif dalam kesejahteraan umat.'}
                </p>
              </div>
              <div className="bg-green-50 p-6 rounded-2xl border border-green-100">
                <h3 className="text-lg font-bold text-green-800 mb-3">🚀 Misi</h3>
                {aboutUs?.mission ? (
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
                    {aboutUs.mission}
                  </p>
                ) : (
                  <ul className="space-y-1.5 text-sm text-gray-700">
                    <li className="flex items-start gap-2"><span className="text-green-600 mt-1">•</span> Mendorong Pemberdayaan Ekonomi</li>
                    <li className="flex items-start gap-2"><span className="text-green-600 mt-1">•</span> Pendidikan dan Literasi Keuangan Syariah</li>
                    <li className="flex items-start gap-2"><span className="text-green-600 mt-1">•</span> Kolaborasi dan Sinergi Antar Komunitas</li>
                    <li className="flex items-start gap-2"><span className="text-green-600 mt-1">•</span> Pengembangan Usaha Berbasis Syariah</li>
                    <li className="flex items-start gap-2"><span className="text-green-600 mt-1">•</span> Kepedulian Sosial dan Amal</li>
                  </ul>
                )}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Bottom Navigation */}
      <NavigationButton />
    </div>
  );
};

export default Home;
